import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Avatar, Flex, Layout, Menu, Modal, theme } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import NProgress from 'nprogress'
import { inits } from 'app-redux/actions'
import {
  closeShiftModalOpenSelector,
  closeShiftModalSuccessSelector,
  loadingInitSelector,
  locationSelector,
  outletSelector,
  shiftSelector,
  userSelector,
  isLockedSelector,
  loadedInitSelector,
  selectedPosOutletPrintStyleLogoBase64Selector,
  selectedPosOutletPrintStyleLogoSelector,
  outletsSelector,
  websiteIdSelector,
  posZeroLoadingSelector,
  catalogSyncedEndpointSelector,
} from 'app-redux/selectors'
import { isEmpty } from 'lodash-es'
import { useIntl } from 'react-intl'
import { logout } from 'app-redux/user/actions'
import { Redirect } from 'react-router-dom'
import { useTotalOrderPerOutlet } from 'utils/queries'
import { POS_ONLINE_HEADER } from 'utils/static'
import useNavigation from '@/utils/hooks/useNavigation'
import useMemoizePermissions from '@/utils/hooks/useMemoizePermissions'
import useFullscreen from '@/utils/hooks/useFullscreen'
import { usePusher } from '@/utils/hooks/usePusher'
import CloseShift from '@/components/apps/Cashier/CloseShift'
import {
  setCloseShiftModalOpen,
  setCloseShiftModalSuccess,
  setOutlet,
  // setShift,
  updatePrintStyleLogoBase64,
} from '@/redux/cashierSlice'
import CloseShiftSuccess from '@/components/apps/Cashier/CloseShift/CloseShiftSuccess'
import OutletSelectorPage from '@/components/apps/Cashier/OutletSelectorPage'
import LockScreen from '@/components/apps/Cashier/LockScreen'
import RegisterDevicePage from '@/components/apps/Cashier/RegisterDevice'
import CatalogSyncGate from '@/components/apps/Cashier/ZeroLoading/CatalogSyncGate'
import ZeroLoadingStatusBadge from '@/components/apps/Cashier/ZeroLoading/ZeroLoadingStatusBadge'
import usePosCatalogSync from '@/utils/hooks/usePosCatalogSync'
import usePosAsyncQueueSync from '@/utils/hooks/usePosAsyncQueueSync'
import usePosDeviceGate from '@/utils/hooks/usePosDeviceGate'
import { flushQueue } from '@/utils/posLocalDb/asyncUploader'
import { getActiveEndpoint } from '@/utils/posLocalDb/queueContext'
import { useAsyncQueueStatus } from '@/utils/posLocalDb/queueVersion'
// import SoftLaunching from '@/components/apps/Cashier/SoftLaunching'
// import { websiteIdSelector } from '@/redux/selectors'
import { isTokenExist } from '@/utils/cookies'
import { toolsBase64Image } from '@/utils/apis/tools'
import { generateCardName } from '@/utils/helper'
import { qtyToCurrency } from '@/utils/formatting'
import styles from './style.module.scss'

const { Sider, Content } = Layout

// Ikon menu ilustratif dari file SVG (pola sama seperti MenuLeft), agar senada dengan menu utama.
const MENU_ICON_BASE = 'resources/images/icons/menu-v2'

const IllusIcon = ({ name }) => (
  <img className={styles.illusIcon} src={`${MENU_ICON_BASE}/${name}.svg`} alt="" />
)

// const SOFT_LAUNCHING_WEB_POS = [
//   165615, // Prameswari champion
//   1376, // Benang Raja
// ]

const CashierLayout = ({ children }) => {
  const dispatch = useDispatch()
  const { token } = theme.useToken()
  const { formatMessage } = useIntl()
  const { onNavigate } = useNavigation()
  const { loading } = useSelector(loadingInitSelector)
  const loadedInit = useSelector(loadedInitSelector)
  const user = useSelector(userSelector)
  const location = useSelector(locationSelector)
  const printStyleLogoBase64 = useSelector(selectedPosOutletPrintStyleLogoBase64Selector)
  const printStyleLogo = useSelector(selectedPosOutletPrintStyleLogoSelector)
  const outlets = useSelector(outletsSelector)
  const websiteId = useSelector(websiteIdSelector)
  const posZeroLoading = useSelector(posZeroLoadingSelector)
  const catalogSyncedEndpoint = useSelector(catalogSyncedEndpointSelector)
  const posPermissions = useMemoizePermissions({ resource: 'pos', recursive: true })

  // Mode Kasir Zero-Loading: sync katalog & antrean transaksi di latar belakang.
  usePosCatalogSync()
  usePosAsyncQueueSync()
  const queueCounts = useAsyncQueueStatus(posZeroLoading)
  const pendingSyncCount = queueCounts.pending + queueCounts.sent + queueCounts.failed
  const canViewReport = !!posPermissions?.pos_reports_sales_summary

  const contentRef = useRef(null)

  const outlet = useSelector(outletSelector)

  // Pendaftaran device POS dicek setelah outlet dipilih (bukan saat login).
  const deviceGate = usePosDeviceGate(loadedInit && !isEmpty(outlet))

  const { data: totalOrderData, refetch: refetchTotalOrder } = useTotalOrderPerOutlet(
    { outlet_id: outlet?.id },
    { enabled: !!outlet?.id },
    POS_ONLINE_HEADER,
  )
  const totalOrder = totalOrderData?.total || 0

  // Listener Pusher: refetch total order saat ada order masuk dari web order POS Kledo
  const channelName = websiteId ? `private-pos-sync-changed-${websiteId}` : null
  const { channelRef } = usePusher(channelName)

  const handlePosSyncChanged = useCallback(
    (data) => {
      const { sync_type: syncType, sync_model: syncModel } = data?.payload || {}
      // Hanya refetch saat ada perubahan data order dari web order POS Kledo
      if (syncType === 'model' && syncModel === 'orders') {
        refetchTotalOrder()
      }
    },
    [refetchTotalOrder],
  )

  useEffect(() => {
    const eventName = 'App\\Events\\PosSyncChanged'
    const channel = channelRef.current
    if (!channel) {
      return undefined
    }
    channel.unbind(eventName)
    channel.bind(eventName, handlePosSyncChanged)
    return () => {
      channel.unbind(eventName)
    }
  }, [channelRef, channelName, handlePosSyncChanged])
  const closeShiftModalOpen = useSelector(closeShiftModalOpenSelector)
  const closeShiftModalSuccess = useSelector(closeShiftModalSuccessSelector)
  const shift = useSelector(shiftSelector)
  const isLocked = useSelector(isLockedSelector)

  const { isFullscreen, toggleFullscreen } = useFullscreen()

  const handleCloseCloseShift = useCallback(() => {
    dispatch(setCloseShiftModalOpen(false))
  }, [dispatch])

  const handleCloseCloseShiftSuccess = useCallback(() => {
    dispatch(setCloseShiftModalSuccess({ open: false, data: null }))
    // dispatch(setShift({}))
  }, [dispatch])

  const handleLogout = useCallback(() => {
    // Masih ada transaksi Zero-Loading yang belum tersinkron: baru terkirim saat kasir
    // ini login lagi di browser ini (antrean di-scope per user) → tawarkan sinkron dulu.
    if (posZeroLoading && pendingSyncCount > 0) {
      Modal.confirm({
        title: formatMessage({ id: 'pos.logout.pending_title' }),
        content: formatMessage({ id: 'pos.logout.pending_desc' }, { count: pendingSyncCount }),
        okText: formatMessage({ id: 'pos.logout.sync_first' }),
        cancelText: formatMessage({ id: 'pos.logout.logout_anyway' }),
        onOk: () => {
          flushQueue()
          onNavigate('/cashier/settings/sync-history')
        },
        onCancel: (close) => {
          if (typeof close === 'function') {
            close()
          }
          dispatch(logout())
        },
      })
      return
    }
    Modal.confirm({
      title: formatMessage({ id: 'topBar.profileMenu.logout' }),
      content: formatMessage({ id: 'popconfirm.title.logout' }),
      okText: formatMessage({ id: 'button.yes' }),
      cancelText: formatMessage({ id: 'button.no' }),
      onOk: () => {
        dispatch(logout())
        // onNavigate('/user/login')
      },
    })
  }, [dispatch, formatMessage, onNavigate, pendingSyncCount, posZeroLoading])

  const handleChangeOutlet = useCallback(() => dispatch(setOutlet(null)), [dispatch])

  const menu = useMemo(() => {
    const _menu = [
      {
        key: 'cashier',
        icon: <IllusIcon name="action-kasir" />,
        label: formatMessage({ id: 'cashier' }),
        onClick: () => {
          onNavigate('/cashier')
        },
      },
      {
        key: 'transaction',
        icon: (
          <span className={styles.transactionIcon}>
            <IllusIcon name="receipt" />
            {totalOrder > 0 && (
              <span className={styles.totalOrderBadge}>{qtyToCurrency(totalOrder)}</span>
            )}
          </span>
        ),
        label: formatMessage({ id: 'report.transactions' }),
        onClick: () => {
          onNavigate('/cashier/transactions')
        },
      },
      ...(canViewReport
        ? [
            {
              key: 'reports',
              icon: <IllusIcon name="reports" />,
              label: formatMessage({ id: 'posReport.reports_menu' }),
              onClick: () => {
                onNavigate('/cashier/reports')
              },
            },
          ]
        : []),
      {
        type: 'divider',
        key: 'divider-2',
      },
      {
        key: 'settings',
        icon: <IllusIcon name="gear" />,
        label: formatMessage({ id: 'kledoPos.settings_title' }),
        onClick: () => {
          onNavigate('/cashier/settings')
        },
      },
      {
        key: 'fullscreen',
        icon: <IllusIcon name={isFullscreen ? 'fullscreen-compress' : 'fullscreen-expand'} />,
        label: isFullscreen
          ? formatMessage({ id: 'exitFullscreen' })
          : formatMessage({ id: 'fullscreen' }),
        onClick: toggleFullscreen,
      },
    ]
    return _menu
  }, [formatMessage, onNavigate, isFullscreen, toggleFullscreen, totalOrder, canViewReport])

  const bottomMenu = useMemo(() => {
    const _bottomMenu = []
    if (!isEmpty(shift)) {
      _bottomMenu.push({
        key: 'close_shift',
        label: formatMessage({ id: 'close_shift' }),
        icon: <IllusIcon name="close-shift" />,
        onClick: () => dispatch(setCloseShiftModalOpen(true)),
      })
    } else {
      if (outlets.length > 1) {
        _bottomMenu.push({
          key: 'outlet_selector',
          label: formatMessage({ id: 'change_outlet' }),
          icon: <IllusIcon name="store" />,
          onClick: () => {
            dispatch(setOutlet(null))
          },
        })
      }
      _bottomMenu.push({
        key: 'logout',
        label: 'Logout',
        icon: <IllusIcon name="action-logout" />,
        onClick: handleLogout,
      })
    }
    return _bottomMenu
  }, [shift, formatMessage, dispatch, outlets.length, handleLogout])

  // Tutup modal CloseShift success ketika user pindah halaman tanpa menutup modal
  const prevPathnameRef = useRef(location?.pathname)
  useEffect(() => {
    const currentPath = location?.pathname || ''
    if (prevPathnameRef.current !== currentPath && closeShiftModalSuccess.open) {
      dispatch(setCloseShiftModalSuccess({ open: false, data: null }))
    }
    prevPathnameRef.current = currentPath
  }, [location?.pathname, closeShiftModalSuccess.open, dispatch])

  // Menentukan selectedKeys berdasarkan pathname
  const selectedKeys = useMemo(() => {
    const pathname = location?.pathname || ''

    // Mapping pathname ke key menu
    if (pathname === '/dashboard' || pathname === '/') {
      return ['dashboard']
    }
    if (pathname.startsWith('/cashier/transactions')) {
      return ['transaction']
    }
    if (pathname.startsWith('/cashier/settings')) {
      return ['settings']
    }
    if (pathname.startsWith('/cashier/reports')) {
      return ['reports']
    }
    if (pathname.startsWith('/cashier')) {
      return ['cashier']
    }

    return []
  }, [location?.pathname])

  // Loader init
  useEffect(() => {
    if (loading) {
      NProgress.start()
    } else {
      NProgress.done()
    }
  }, [loading])

  useEffect(() => {
    // Jika init belum di load, maka di ambil dari api
    const loadInits = async () => {
      const onInits = () => dispatch(inits())
      onInits()
    }

    if (!user?.id) {
      loadInits()
    }
  }, [user, dispatch])

  useEffect(() => {
    if (
      loadedInit &&
      !isEmpty(outlet) &&
      isEmpty(printStyleLogoBase64) &&
      !isEmpty(printStyleLogo)
    ) {
      const convertPrintStyleLogoToBase64 = async () => {
        const response = await toolsBase64Image({ url: printStyleLogo })
        if (response.data.success) {
          if (!isEmpty(response.data.data)) {
            dispatch(
              updatePrintStyleLogoBase64({
                outlet_id: outlet.id,
                base64Image: response.data.data,
              }),
            )
          }
        }
      }
      convertPrintStyleLogoToBase64()
    }
  }, [dispatch, loadedInit, outlet, printStyleLogo, printStyleLogoBase64])

  if (!isTokenExist()) {
    return <Redirect to="/user/login" />
  }

  // if (!SOFT_LAUNCHING_WEB_POS.includes(websiteId) && import.meta.env.PROD && loadedInit) {
  //   return <SoftLaunching />
  // }

  if (isEmpty(outlet) && loadedInit) {
    return <OutletSelectorPage />
  }

  // Browser belum terdaftar sebagai device POS → wajib daftar dulu (sama dengan mobile).
  // BE yang tidak mengirim `pos_device` / cek gagal dibiarkan lolos.
  if (deviceGate.status !== 'ready') {
    return (
      <RegisterDevicePage
        deviceId={deviceGate.deviceId}
        outlet={outlet}
        checking={deviceGate.status === 'checking'}
        onRegistered={deviceGate.markRegistered}
        onChangeOutlet={outlets.length > 1 ? handleChangeOutlet : undefined}
        onLogout={handleLogout}
      />
    )
  }

  if (isLocked && loadedInit) {
    return <LockScreen />
  }

  // Katalog lokal tenant ini belum tersinkron → gate sinkronisasi sekali per tenant.
  if (loadedInit && posZeroLoading && catalogSyncedEndpoint !== getActiveEndpoint()) {
    return <CatalogSyncGate onLogout={handleLogout} />
  }

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden', background: token.colorBgLayout }}>
      <Sider
        collapsed
        collapsedWidth={80}
        theme="light"
        className="cashier-sider"
        style={{ zIndex: 1001, position: 'relative', background: token.colorBgContainer }}
      >
        <div
          className={styles.sidebarHeader}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (isFullscreen) {
              toggleFullscreen()
            }

            setTimeout(() => onNavigate('/dashboard'), 400)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (isFullscreen) {
                toggleFullscreen()
              }
              setTimeout(() => onNavigate('/dashboard'), 400)
            }
          }}
        >
          <Avatar shape="circle" src={outlet?.photo} size={50}>
            {generateCardName(outlet?.name || '')}
          </Avatar>
        </div>
        <Flex justify="space-between" vertical style={{ height: 'calc(100vh - 80px)' }}>
          <div
            className={styles.menuMainWrapper}
            style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          >
            <Menu
              selectedKeys={selectedKeys}
              theme="light"
              mode="inline"
              items={menu}
              className={`${styles.menu} ${styles.menuMain}`}
            />
          </div>
          <div style={{ flexShrink: 0 }}>
            {posZeroLoading && <ZeroLoadingStatusBadge counts={queueCounts} />}
            <Menu theme="light" mode="inline" items={bottomMenu} className={styles.menu} />
          </div>
        </Flex>
      </Sider>
      <Layout style={{ height: '100vh', overflow: 'hidden', background: token.colorBgLayout }}>
        <Content
          ref={contentRef}
          id="cashier-content-area"
          className={styles.content}
          style={{
            background: token.colorBgLayout,
          }}
        >
          {children}
        </Content>
      </Layout>
      {closeShiftModalOpen && (
        <CloseShift user={user} onCancel={handleCloseCloseShift} open={closeShiftModalOpen} />
      )}
      {closeShiftModalSuccess.open && (
        <CloseShiftSuccess
          data={closeShiftModalSuccess.data}
          onCancel={handleCloseCloseShiftSuccess}
          open={closeShiftModalSuccess.open}
        />
      )}
    </Layout>
  )
}

export default CashierLayout
